"use client";

import { Filter, Pin, Save, Star } from "lucide-react";
import { makeFunctionReference } from "convex/server";
import { useMutation, useQuery } from "convex/react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { QuerySkeleton } from "@/components/ui/skeleton";
import type { LifecycleState } from "@/core/tma";
import { getEmptyListState } from "@/core/ui/empty-states";
import {
  applyLoadView,
  buildFilterSummary,
  togglePinnedLoad,
  type LoadViewFilters,
  type LoadViewLoad,
  type LoadViewSort,
} from "@/core/console/load-views";

interface SavedLoadViewRow {
  _id: string;
  tenantId: string;
  userId: string;
  name: string;
  filters: LoadViewFilters;
  sort: LoadViewSort;
  isDefault: boolean;
}

interface PinnedLoadRow {
  _id: string;
  tenantId: string;
  userId: string;
  loadId: string;
  reference: string;
  pinnedAt: number;
}

const listWorkspace = makeFunctionReference<
  "query",
  { tenantId: string; userId: string },
  { views: SavedLoadViewRow[]; pinnedLoads: PinnedLoadRow[] }
>("loadViews:listWorkspace");
const saveView = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    userId: string;
    name: string;
    filters: LoadViewFilters;
    sort: LoadViewSort;
    isDefault?: boolean;
    actor: "dispatcher";
    actorId: string;
  },
  { viewId: string }
>("loadViews:saveView");
const pinLoad = makeFunctionReference<
  "mutation",
  { tenantId: string; userId: string; loadId: string; actor: "dispatcher"; actorId: string },
  { pinId: string }
>("loadViews:pinLoad");
const unpinLoad = makeFunctionReference<
  "mutation",
  { tenantId: string; userId: string; loadId: string; actor: "dispatcher"; actorId: string },
  { deleted: boolean }
>("loadViews:unpinLoad");

const lifecycleOptions: LifecycleState[] = ["covered", "pickup_confirmed", "in_transit", "delivered", "pod_collecting"];

export function SavedViewsPanel({
  tenantId,
  userId,
  loads,
}: {
  tenantId: string;
  /** Server-derived authenticated user id: undefined = resolving, null = signed out. */
  userId?: string | null;
  loads: LoadViewLoad[];
}) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <SavedViewsLocal tenantId={tenantId} userId="local-preview" loads={loads} />;
  }
  if (userId === undefined) {
    return (
      <section id="saved-views" className="space-y-4">
        <QuerySkeleton surface="savedViews" />
      </section>
    );
  }
  if (userId === null) {
    // Fail closed: never persist views or pins under a fabricated identity.
    return (
      <section id="saved-views" className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Saved Views</h2>
          <p className="text-sm text-muted-foreground">
            Sign in with an active tenant membership to load and save your views.
          </p>
        </div>
      </section>
    );
  }
  return <SavedViewsLive tenantId={tenantId} userId={userId} loads={loads} />;
}

function SavedViewsLive(props: { tenantId: string; userId: string; loads: LoadViewLoad[] }) {
  const workspace = useQuery(listWorkspace, { tenantId: props.tenantId, userId: props.userId });
  const saveViewMutation = useMutation(saveView);
  const pinLoadMutation = useMutation(pinLoad);
  const unpinLoadMutation = useMutation(unpinLoad);
  if (workspace === undefined) {
    return (
      <section id="saved-views" className="space-y-4">
        <QuerySkeleton surface="savedViews" />
      </section>
    );
  }
  return (
    <SavedViewsContent
      key={`${workspace?.views.length ?? 0}:${workspace?.pinnedLoads.length ?? 0}`}
      {...props}
      initialViews={workspace.views}
      initialPinnedLoadIds={workspace.pinnedLoads.map((pin) => pin.loadId)}
      onSaveView={(input) =>
        saveViewMutation({
          tenantId: props.tenantId,
          userId: props.userId,
          name: input.name,
          filters: input.filters,
          sort: input.sort,
          actor: "dispatcher",
          actorId: props.userId,
        })
      }
      onPinLoad={(loadId, currentlyPinned) => {
        const args = {
          tenantId: props.tenantId,
          userId: props.userId,
          loadId,
          actor: "dispatcher" as const,
          actorId: props.userId,
        };
        return currentlyPinned ? unpinLoadMutation(args) : pinLoadMutation(args);
      }}
    />
  );
}

function SavedViewsLocal(props: { tenantId: string; userId: string; loads: LoadViewLoad[] }) {
  const views = useMemo<SavedLoadViewRow[]>(() => [], []);
  const pinnedLoadIds = useMemo<string[]>(() => [], []);
  return <SavedViewsContent {...props} initialViews={views} initialPinnedLoadIds={pinnedLoadIds} />;
}

function SavedViewsContent({
  tenantId,
  userId,
  loads,
  initialViews,
  initialPinnedLoadIds,
  onSaveView,
  onPinLoad,
}: {
  tenantId: string;
  userId: string;
  loads: LoadViewLoad[];
  initialViews: SavedLoadViewRow[];
  initialPinnedLoadIds: string[];
  onSaveView?: (input: { name: string; filters: LoadViewFilters; sort: LoadViewSort }) => Promise<unknown>;
  onPinLoad?: (loadId: string, currentlyPinned: boolean) => Promise<unknown>;
}) {
  const [views, setViews] = useState(initialViews);
  const [pinnedLoadIds, setPinnedLoadIds] = useState(initialPinnedLoadIds);
  const [filters, setFilters] = useState<LoadViewFilters>(initialViews.find((view) => view.isDefault)?.filters ?? {});
  const [sort, setSort] = useState<LoadViewSort>({ field: "pickupWindowStart", direction: "asc" });
  const [viewName, setViewName] = useState("My dispatch view");
  const filteredLoads = useMemo(() => applyLoadView(loads, filters, sort), [filters, loads, sort]);
  const pinnedLoads = loads.filter((load) => pinnedLoadIds.includes(load.id));

  async function persistView() {
    const row: SavedLoadViewRow = {
      _id: `local_${viewName.toLowerCase().replaceAll(" ", "_")}`,
      tenantId,
      userId,
      name: viewName,
      filters,
      sort,
      isDefault: views.length === 0,
    };
    setViews((current) => [row, ...current.filter((view) => view.name !== viewName)]);
    await onSaveView?.({ name: viewName, filters, sort });
  }

  async function togglePin(loadId: string) {
    const currentlyPinned = pinnedLoadIds.includes(loadId);
    setPinnedLoadIds((current) => togglePinnedLoad(current, loadId));
    await onPinLoad?.(loadId, currentlyPinned);
  }

  return (
    <section id="saved-views" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Saved Views</h2>
          <p className="text-sm text-muted-foreground">Per-user filters and pinned active loads.</p>
        </div>
        <Badge variant="info">{filteredLoads.length} visible</Badge>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter size={16} />
            Filters
          </CardTitle>
          <div className="flex items-center gap-2">
            <input
              value={viewName}
              onChange={(event) => setViewName(event.target.value)}
              className="h-9 w-44 rounded-md border border-border bg-card px-3 text-sm outline-none"
            />
            <Button variant="secondary" onClick={persistView}>
              <Save size={16} />
              Save
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <input
              value={filters.search ?? ""}
              onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value || undefined }))}
              placeholder="Search"
              className="h-9 rounded-md border border-border bg-card px-3 text-sm outline-none"
            />
            <select
              value={filters.lifecycleStates?.[0] ?? ""}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  lifecycleStates: event.target.value ? [event.target.value as LifecycleState] : undefined,
                }))
              }
              className="h-9 rounded-md border border-border bg-card px-3 text-sm outline-none"
            >
              <option value="">Any status</option>
              {lifecycleOptions.map((status) => (
                <option key={status} value={status}>
                  {status.replaceAll("_", " ")}
                </option>
              ))}
            </select>
            <select
              value={`${sort.field}:${sort.direction}`}
              onChange={(event) => {
                const [field, direction] = event.target.value.split(":") as [LoadViewSort["field"], LoadViewSort["direction"]];
                setSort({ field, direction });
              }}
              className="h-9 rounded-md border border-border bg-card px-3 text-sm outline-none"
            >
              <option value="pickupWindowStart:asc">Pickup first</option>
              <option value="deliveryWindowStart:asc">Delivery first</option>
              <option value="risk:desc">Risk first</option>
              <option value="reference:asc">Reference</option>
            </select>
            <label className="flex h-9 items-center gap-2 rounded-md border border-border px-3 text-sm">
              <input
                type="checkbox"
                checked={!!filters.exceptionOnly}
                onChange={(event) => setFilters((current) => ({ ...current, exceptionOnly: event.target.checked || undefined }))}
              />
              Exceptions
            </label>
          </div>
          {views.length === 0 ? (
            <EmptyState className="p-4" onAction={persistView} {...getEmptyListState("savedViews")} />
          ) : (
            <div className="flex flex-wrap gap-2">
              {views.map((view) => (
                <button
                  key={view._id}
                  type="button"
                  onClick={() => {
                    setFilters(view.filters);
                    setSort(view.sort);
                  }}
                  className="inline-flex h-8 items-center gap-2 rounded-md border border-border px-3 text-sm hover:bg-muted"
                >
                  {view.isDefault ? <Star size={14} /> : null}
                  {view.name}
                </button>
              ))}
            </div>
          )}
          {pinnedLoads.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {pinnedLoads.map((load) => (
                <a
                  key={load.id}
                  href={`/loads/${load.id}?tenantId=${tenantId}`}
                  className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-muted px-3 text-sm"
                >
                  <Pin size={14} />
                  {load.reference}
                </a>
              ))}
            </div>
          ) : (
            <EmptyState className="p-4" {...getEmptyListState("pinnedLoads")} />
          )}
          <div className="grid gap-2">
            {filteredLoads.length === 0 ? (
              <EmptyState
                className="p-4"
                onAction={() => setFilters({})}
                {...getEmptyListState("filteredLoads")}
              />
            ) : (
              filteredLoads.slice(0, 5).map((load) => (
                <div key={load.id} className="grid gap-3 rounded-md border border-border p-3 md:grid-cols-[1fr_auto]">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{load.reference}</span>
                      <Badge>{load.lifecycleState.replaceAll("_", " ")}</Badge>
                      {load.exceptionCount ? <Badge variant="warn">{load.exceptionCount} exception</Badge> : null}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {load.customerName} / {load.origin.city}, {load.origin.state} to {load.destination.city},{" "}
                      {load.destination.state}
                    </div>
                  </div>
                  <Button variant="ghost" onClick={() => togglePin(load.id)}>
                    <Pin size={16} />
                    {pinnedLoadIds.includes(load.id) ? "Unpin" : "Pin"}
                  </Button>
                </div>
              ))
            )}
          </div>
          <div className="text-xs text-muted-foreground">{buildFilterSummary(filters) || "All active loads"}</div>
        </CardContent>
      </Card>
    </section>
  );
}
